import type {
  ObservationsMapParameterDisplayGroup,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationBody,
  ParametersUnit,
} from "@/lib/api/generated/types";
import type { ParameterWithUnits } from "@/lib/parameters/parameter-units.utils";
import {
  configuredTimeSeriesParameterIdSet,
  intersectUnitCodes,
  pickAutoUnitCode,
  resolveUnitFromCatalog,
} from "@/lib/parameters/parameter-units.utils";

/**
 * Loaded chart config (`unit`) or an already-converted save body (`unit_code`).
 * Save normalizers run more than once on the same payload (the map dialogs
 * normalize on submit, then `isUnchangedWidgetUpdate` normalizes again), so the
 * conversion accepts its own output.
 */
type ParameterTimeSeriesConfigurationLike =
  | ParameterTimeSeriesConfiguration
  | ParameterTimeSeriesConfigurationBody;

/**
 * `unit` is nullable in the spec and absent once converted, so a missing unit
 * yields an empty code instead of throwing; the CMS already blocks saving a
 * group without a unit.
 */
function readUnitCode(config: ParameterTimeSeriesConfigurationLike): string {
  const record = config as { unit?: ParametersUnit | null; unit_code?: string };
  return record.unit?.code ?? record.unit_code ?? "";
}

export function toParameterTimeSeriesConfigurationBody(
  config: ParameterTimeSeriesConfigurationLike,
): ParameterTimeSeriesConfigurationBody {
  return {
    title: config.title,
    visualization_type: config.visualization_type,
    unit_code: readUnitCode(config),
    series: config.series,
    ...(config.thresholds && config.thresholds.length > 0
      ? { thresholds: config.thresholds }
      : {}),
  };
}

export function normalizeParameterTimeSeriesConfigurationsForSave(
  configurations: ParameterTimeSeriesConfigurationLike[] | undefined,
): ParameterTimeSeriesConfigurationBody[] {
  if (!Array.isArray(configurations)) return [];
  return configurations.map(toParameterTimeSeriesConfigurationBody);
}

export function backfillParameterTimeSeriesUnit(
  config: ParameterTimeSeriesConfiguration,
  catalog: Array<ParameterWithUnits & { id: string }>,
): ParameterTimeSeriesConfiguration {
  if (config.unit?.code) return config;

  const seriesParameters = config.series
    .map((series) => catalog.find((parameter) => parameter.id === series.parameter_id))
    .filter((parameter): parameter is ParameterWithUnits & { id: string } => !!parameter);

  const overlappingUnits = intersectUnitCodes(seriesParameters);
  const autoCode = pickAutoUnitCode(overlappingUnits, undefined);
  const fallbackCode =
    autoCode ?? seriesParameters[0]?.units[0]?.code ?? config.unit?.code ?? "";
  const unit =
    resolveUnitFromCatalog(
      overlappingUnits.length > 0 ? overlappingUnits : (seriesParameters[0]?.units ?? []),
      fallbackCode,
    ) ?? config.unit;

  if (!unit) return config;

  return { ...config, unit };
}

export function backfillParameterTimeSeriesConfigurations(
  configurations: ParameterTimeSeriesConfiguration[] | undefined,
  catalog: Array<ParameterWithUnits & { id: string }>,
): ParameterTimeSeriesConfiguration[] {
  if (!Array.isArray(configurations)) return [];
  return configurations.map((config) => backfillParameterTimeSeriesUnit(config, catalog));
}

export function stripDisplayGroupParametersWithoutTimeSeries(
  groups: ObservationsMapParameterDisplayGroup[] | undefined,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[] | undefined,
): ObservationsMapParameterDisplayGroup[] {
  if (!Array.isArray(groups)) return [];

  const configuredIds = configuredTimeSeriesParameterIdSet(timeSeriesConfigurations ?? []);

  return groups
    .map((group) => ({
      ...group,
      parameter_ids: group.parameter_ids.filter((id) => configuredIds.has(id)),
    }))
    .filter((group) => group.parameter_ids.length > 0);
}

export function formatUnitLabel(unit: ParametersUnit | undefined): string {
  if (!unit) return "";
  return unit.name ?? unit.symbol ?? unit.code;
}
